import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Center,
  Container,
  Group,
  Loader,
  Text,
} from '@mantine/core';
import { AlertTriangle, Clock } from 'react-feather';
import { useNavigate, useParams } from 'react-router-dom';
import {
  participantActiveSessionApi,
  type ParticipantActiveSessionState,
} from '../../../services/api/participantActiveSession';
import { useFeatureFlag } from '../../../stores/featureFlags';
import { useSelectedCohortAccess } from '../../../stores/participantCohort';
import { FeaturesDisabledPage } from '../../FeaturesDisabledPage';
import { ActiveSessionTopBar } from './components/ActiveSessionTopBar';
import { useAiListeningLiveState } from '../../../hooks/useAiListeningLiveState';
import { useWorkerInterval } from '../../../hooks/useWorkerInterval';
import { ActiveSessionSlideCard } from './components/ActiveSessionSlideCard';
import { ParticipantSlideCounter } from './components/ParticipantSlideCounter';
import { NotesPanel, PANEL_WIDTH } from './components/NotesPanel';
import { NotesPencilFab } from './components/NotesPencilFab';
import { CommitmentArea } from './components/CommitmentArea';
import { AiDisclosureModal } from '../../../components/session/AiDisclosureModal';
import { useAuthStore } from '../../../stores/auth';
import {
  hasParticipantAiAck,
  setParticipantAiAck,
} from '../../../utils/participantAiAck';
import type { PresenceMode } from '../../../services/api/waitingRoom';

/**
 * Participant Active Session screen (MED-805 / E3.3).
 *
 * Immersive dark layout rendered OUTSIDE `ParticipantLayout` so the Medley
 * app chrome stays out of the way (AC #2). The page polls the participant
 * active-state endpoint at `POLL_INTERVAL_MS` to keep the slide content and
 * commitment trigger in sync with the leader's actions inside the AC #6 / IV2
 * 2-second SLA.
 *
 * Lifecycle:
 *   - Mount → fetch `/active-state`. When the leader ends the session it flips
 *     to `completed` → route the participant into the post-session flow
 *     (MED-806 / E3.4). Any other non-active status (never started, cancelled,
 *     rescheduled, or 403/404) has no post-session surface, so navigate back to
 *     `/participant` and let the dashboard own the recovery UX.
 *   - While in_progress → poll every POLL_INTERVAL_MS.
 *   - Exit → AC #4 — navigate to `/participant`. From there the participant
 *     can re-join via the dashboard while the session remains in progress.
 */

const POLL_INTERVAL_MS = 2_000;

export interface ParticipantActiveSessionPageProps {
  /**
   * MED-1030 — DEV-only static preview. Seeds the payload and bypasses the
   * feature-flag gate, the active-state fetch/poll, and the live AI socket so
   * the immersive screen renders from an in-memory fixture (`/dev/participant-
   * session`). Compiled out of production builds (gated at the route).
   */
  preview?: { state: ParticipantActiveSessionState };
}

export function ParticipantActiveSessionPage({
  preview,
}: ParticipantActiveSessionPageProps = {}) {
  const isPreview = !!preview;
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const isFlagOn = useFeatureFlag('live_session_delivery');
  // MED-1307 (MDF-184): this page sits outside ParticipantLayout, so it applies
  // the cohort participant-features and app-access gate itself.
  const cohortAccess = useSelectedCohortAccess();

  const [payload, setPayload] = useState<ParticipantActiveSessionState | null>(
    preview?.state ?? null
  );
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState<string | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const userId = useAuthStore(s => s.user?.id);
  const [aiAcknowledged, setAiAcknowledged] = useState(() =>
    hasParticipantAiAck(userId, sessionId)
  );

  // MDF-242 - the waiting room is not the only way in: a direct link to this
  // page skipped the AI disclosure. Gate here too, with the same conditions as
  // the waiting room (modality flag AND per-session listening toggle). Until
  // the participant acknowledges, show only the disclosure, keep the AI socket
  // closed, and stop the poll, because the poll is the presence heartbeat.
  const needsAiDisclosure =
    !isPreview &&
    !!payload &&
    payload.ai_features_enabled &&
    payload.ai_listening_enabled &&
    !aiAcknowledged;

  // MDF-242: same presence rule as the waiting room. Until the participant
  // acknowledges, the server records presence only when the session needs no
  // AI disclosure, so an AI-off session is recorded on the first read.
  const presenceModeRef = useRef<PresenceMode>(
    aiAcknowledged ? 'always' : 'unless_ai_disclosure'
  );
  presenceModeRef.current = aiAcknowledged ? 'always' : 'unless_ai_disclosure';

  // MED-953 — live pause state from session-intelligence (socket; the rest of
  // this page polls medley-api, but the pause signal lives in SI memory and
  // must flip in real time per AC4). Runs unconditionally (hook order) and
  // only opens a socket once the payload says both AI gates are on.
  const { paused: aiPaused } = useAiListeningLiveState({
    sessionId,
    enabled:
      !isPreview &&
      !needsAiDisclosure &&
      Boolean(payload?.ai_features_enabled && payload?.ai_listening_enabled),
  });

  const fetchPayload = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!sessionId) return;
      if (!opts.silent) setLoading(true);
      try {
        const result = await participantActiveSessionApi.getActiveState(
          sessionId,
          { presence: presenceModeRef.current }
        );
        setPayload(result);
        setError(null);
        // Status-based redirect — keeps the page from sitting on a stale screen
        // after the leader presses End Session. A `completed` session ran to its
        // end, so route into the post-session flow (MED-806 / E3.4) where the
        // participant leaves feedback, reviews notes, and finalizes commitment.
        // Every other non-active status (never started, cancelled, rescheduled)
        // has no post-session surface, so the dashboard owns the recovery UX.
        if (result.status === 'completed') {
          navigate(`/participant/sessions/${sessionId}/post-session`, {
            replace: true,
          });
        } else if (result.status !== 'in_progress') {
          navigate('/participant', { replace: true });
        }
      } catch (err) {
        const e = err as Error & { status?: number };
        if (e?.status === 403) {
          setError('You are not enrolled in this session.');
        } else if (e?.status === 404) {
          setError('Session not found or unavailable.');
        } else if (!opts.silent) {
          setError(e?.message ?? 'Failed to load the session.');
        }
      } finally {
        if (!opts.silent) setLoading(false);
      }
    },
    [sessionId, navigate]
  );

  useEffect(() => {
    if (isPreview) return;
    void fetchPayload();
  }, [fetchPayload, isPreview]);

  // MED-1260 — drive the recurring poll from a Web Worker timer instead of a
  // main-thread setInterval. This poll doubles as the presence heartbeat: each
  // getActiveState read refreshes session_attendance.last_seen_at (MED-1028
  // heartbeat-on-read). Chrome throttles backgrounded-tab main-thread timers to
  // ~1/min, which starved that heartbeat and made present-but-backgrounded
  // participants (Medley behind Zoom) read as disconnected in the facilitator's
  // roster and commitment count. A worker timer is not background-throttled, so
  // the heartbeat keeps firing regardless of tab foreground/background state.
  useWorkerInterval(
    () => {
      void fetchPayload({ silent: true });
    },
    POLL_INTERVAL_MS,
    !isPreview && !!sessionId && !needsAiDisclosure,
  );

  const handleExit = () => {
    navigate('/participant');
  };

  const handleAcknowledgeAi = () => {
    setParticipantAiAck(userId, sessionId);
    setAiAcknowledged(true);
    // MDF-242: the poll was paused behind the disclosure; record presence and
    // refresh now rather than at the next 2s tick.
    presenceModeRef.current = 'always';
    void fetchPayload({ silent: true });
  };

  // Same destination as the waiting room's "Return to portal".
  const handleDeclineAi = () => {
    navigate(`/participant/sessions/${sessionId}/reflection`, {
      replace: true,
    });
  };

  // MED-1180 — defense-in-depth: also disable when the per-cohort LSD flag is
  // false on the returned payload (backend already 404s a non-LSD cohort).
  if (!isPreview && (!isFlagOn || payload?.live_session_delivery_enabled === false)) {
    return <FeaturesDisabledPage />;
  }

  if (!isPreview && cohortAccess !== 'enabled') {
    return <FeaturesDisabledPage reason={cohortAccess} />;
  }

  if (loading) {
    return (
      <Box style={{ minHeight: '100vh', backgroundColor: '#0E1B33' }}>
        <Center mih="100vh">
          <Loader color="white" />
        </Center>
      </Box>
    );
  }

  if (error || !payload) {
    return (
      <Box style={{ minHeight: '100vh', backgroundColor: '#0E1B33' }}>
        <Container size="md" py="lg">
          <Alert
            icon={<AlertTriangle size={18} />}
            color="red"
            title="Session unavailable"
          >
            <Text size="sm">{error ?? 'Unknown error.'}</Text>
          </Alert>
        </Container>
      </Box>
    );
  }

  if (needsAiDisclosure) {
    return (
      <Box
        data-testid="participant-active-ai-disclosure-gate"
        style={{ minHeight: '100vh', backgroundColor: '#0E1B33' }}
      >
        <AiDisclosureModal
          opened
          role={
            payload.leader.role_label === 'PEER LEADER'
              ? 'peer_leader'
              : 'participant'
          }
          onAcknowledge={handleAcknowledgeAi}
          onDismiss={handleDeclineAi}
          onReturnToPortal={handleDeclineAi}
        />
      </Box>
    );
  }

  // MED-952 — match the absence-notice wording to the leader's role label.
  const leaderLabel =
    payload.leader.role_label === 'PEER LEADER' ? 'peer leader' : 'coach';

  const currentVisuals =
    payload.current_section_index !== null
      ? (payload.sections[payload.current_section_index]?.visuals ?? [])
      : [];
  const hasCurrentVisuals = currentVisuals.length > 0;

  return (
    <Box
      data-testid="participant-active-session-page"
      style={{
        minHeight: '100vh',
        backgroundColor: '#0E1B33',
        color: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        // MED-1165 — push layout: while the notes drawer is open, reserve its
        // width on the right so the top bar + slide stage + commitment card
        // reflow into the space beside the drawer instead of being covered by
        // it (the drawer is a fixed right-edge overlay). Transition matches the
        // drawer's 220ms slide so the reflow reads as one coordinated motion.
        // Returns to full width when the drawer closes (no permanent gap).
        paddingRight: notesOpen ? PANEL_WIDTH : 0,
        transition: 'padding-right 220ms ease-out',
      }}
    >
      <ActiveSessionTopBar
        leader={payload.leader}
        aiFeaturesEnabled={payload.ai_features_enabled}
        aiListeningEnabled={payload.ai_listening_enabled}
        aiPaused={aiPaused}
        meetingLink={payload.meeting_link}
        onExit={handleExit}
      />

      {/* MED-952 AC8/AC9 — coach-absence notice. Appears when no coach is
          present and clears in real time (next 2s poll) when a coach rejoins.
          The session stays in_progress and content remains fully interactive
          (notes/commitments are not locked — resolved Q4). */}
      {!payload.coach_present && (
        <Box
          data-testid="participant-coach-absence-notice"
          style={{
            backgroundColor: 'rgba(233, 162, 59, 0.15)',
            borderBottom: '1px solid #E9A23B',
            padding: '10px 16px',
          }}
        >
          <Group gap={8} justify="center" align="center">
            <Clock size={16} color="#E9A23B" aria-hidden="true" />
            <Text size="sm" fw={500} style={{ color: '#E9A23B' }}>
              Your {leaderLabel} has stepped away for a moment. The session is
              still in progress — hang tight.
            </Text>
          </Group>
        </Box>
      )}

      {/* MED-1274 AC5 — the stage uses the available viewport width instead of
          Mantine's `size="lg"` (1140px) cap, which stranded navy on both sides
          of the stage on wider displays and prevented the visual from growing.
          `fluid` keeps the default 16px inline gutter. */}
      <Container
        fluid
        py="xl"
        style={{
          flexGrow: 1,
          width: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* MED-1030 AC1 — the slide "stage": a rounded lighter-navy gradient
            panel wrapping the white slide card + the display-only pager, in
            place of staging's edge-to-edge card. */}
        <Box
          data-testid="participant-active-slide-stage"
          style={{
            background: 'linear-gradient(180deg, #173256 0%, #0F2138 100%)',
            borderRadius: 20,
            padding: 'clamp(16px, 3vw, 36px)',
            // The short bottom pad leaves room for the slide counter; with no
            // slides there is no counter, so keep the pad even (MDF-243).
            paddingBottom: hasCurrentVisuals ? 16 : 'clamp(16px, 3vw, 36px)',
            // Grow to fill the viewport when content is short (so the screen
            // doesn't leave a dead navy band beneath the card).
            // MDF-243: only when the section has slides. Without slides the
            // stage sizes to its text so the commitment box stays in view.
            flex: hasCurrentVisuals ? 1 : '0 0 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ActiveSessionSlideCard
            sections={payload.sections}
            currentSectionIndex={payload.current_section_index}
            slideDeck={payload.slide_deck}
            cohortId={payload.cohort_id}
            sessionId={payload.session_id}
            currentSlidePage={payload.current_slide_page}
            lastSessionCommitment={payload.last_session_commitment}
          />

          <ParticipantSlideCounter
            visuals={currentVisuals}
            currentSlidePage={payload.current_slide_page}
          />
        </Box>

        <CommitmentArea
          sessionId={payload.session_id}
          active={payload.commitment_section_active}
          preview={isPreview}
        />
      </Container>

      <NotesPanel
        sessionId={payload.session_id}
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        preview={isPreview}
      />
      <NotesPencilFab onOpen={() => setNotesOpen(o => !o)} isOpen={notesOpen} />
    </Box>
  );
}
